import { isMissingBlockError } from '../net/api-error';
import {
  createBlock,
  deleteBlock,
  fetchBlock,
  fetchBlockTree,
  fetchCurrentMember,
  fetchPageDocument,
  updateBlock,
} from './api';
import { copyLlmPrivateFlag } from '@/lib/llm-private/llm-private-api';
import { isWorkspaceRootBlock } from './constants';
import { newBlockId } from './ids';
import type { BlockRecord } from './types';

async function duplicateContentBlocks(
  sourcePageId: string,
  targetPageId: string,
  contentBlocks: BlockRecord[],
): Promise<void> {
  async function cloneChildren(oldParentId: string, newParentId: string): Promise<void> {
    const children = contentBlocks
      .filter((block) => block.parentId === oldParentId)
      .sort((a, b) => a.position - b.position);

    for (const child of children) {
      const newId = newBlockId();
      await createBlock({
        id: newId,
        parentId: newParentId,
        type: child.type,
        content: child.content,
        position: child.position,
      });
      await cloneChildren(child.id, newId);
    }
  }

  await cloneChildren(sourcePageId, targetPageId);
}

export async function duplicatePage(pageId: string, parentId: string): Promise<BlockRecord> {
  const document = await fetchPageDocument(pageId);
  const siblings = await fetchBlockTree(parentId);
  const pageSiblings = siblings.filter((b) => b.type === 'page' || b.type === 'database');
  const baseTitle = document.block.title ?? 'Untitled';
  const copyTitle = baseTitle.endsWith(' (copy)') ? baseTitle : `${baseTitle} (copy)`;

  const newPage = await createBlock({
    id: newBlockId(),
    parentId,
    type: 'page',
    title: copyTitle,
    position: pageSiblings.length,
  });

  await duplicateContentBlocks(pageId, newPage.id, document.contentBlocks);
  await copyLlmPrivateFlag(pageId, newPage.id);
  return newPage;
}

export async function renamePage(pageId: string, title: string): Promise<BlockRecord> {
  const trimmed = title.trim() || 'Untitled';
  return updateBlock(pageId, { title: trimmed });
}

export async function trashPage(pageId: string, known?: BlockRecord): Promise<void> {
  const block = known ?? (await readBlockForTrash(pageId));

  if (block?.parentId == null && block) {
    const member = await fetchCurrentMember();
    if (isWorkspaceRootBlock(block, member.id)) {
      throw new Error('Family and personal workspace pages cannot be deleted.');
    }
  }

  await deleteBlock(pageId, { remote: known ? false : true });
}

async function readBlockForTrash(pageId: string): Promise<BlockRecord | null> {
  try {
    return await fetchBlock(pageId);
  } catch (err) {
    if (!isMissingBlockError(err)) {
      throw err;
    }
    return null;
  }
}

export function promptRename(currentTitle: string): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const next = window.prompt('Rename page', currentTitle);
  if (next === null) {
    return null;
  }
  return next.trim() || 'Untitled';
}
